/*
Per-arbre details loader. Companion to arbresFeatures.ts.

The geojson now ships the bare minimum (id, sk, remarquable, coords) so
cold-load is fast. The "extra" fields — height in m, circumference in
cm, formatted address, arrondissement label — live in this separate
`arbres-details.json` object map, lazily fetched only when:
  - a captured tree's sheet opens (per-arbre details panel),
  - the badge evaluator runs (Géant / Vieux sage / Tourneur / Tour complet),
  - the Profile screen asks for the remarquable count cross-reference.

Shape of the JSON file:
  { "<arbreId>": { "hm": 12, "cm": 75, "addr": "…", "arr": "12e" }, … }
Missing fields are simply absent (the build script skips nulls).
*/

import { fetchJson } from './fetchJson'

const kAssetPath = '/data/arbres-details.json'  // canonical SW-cached asset path

interface RawDetails
{
	hm?: number
	cm?: number
	addr?: string
	arr?: string
}

export interface ArbreDetails
{
	heightMeters: number | null
	circumferenceCm: number | null
	address: string | null
	arrondissement: string | null
}

let fDetails: Map<number, ArbreDetails> | null = null  // memoized id -> details, null until first parse
let fLoading: Promise<Map<number, ArbreDetails>> | null = null  // in-flight loader, dedups concurrent callers

/*
Ensure the details map is loaded. Idempotent. Concurrent callers share
the in-flight Promise. ~5 MB JSON parsed into a Map<number, ArbreDetails>.
*/
export async function ensureArbresDetails(): Promise<Map<number, ArbreDetails>>
{
	if (fDetails) return fDetails
	if (fLoading) return fLoading
	fLoading = (async () => {
		const vRaw = await fetchJson<Record<string, RawDetails>>(kAssetPath)
		const vMap = new Map<number, ArbreDetails>()
		for (const [vKey, vVal] of Object.entries(vRaw))
		{
			const vId = Number(vKey)
			if (!Number.isFinite(vId)) continue  // defensive: skip non-numeric keys (shouldn't happen)
			vMap.set(vId, {
				heightMeters: typeof vVal.hm === 'number' ? vVal.hm : null,
				circumferenceCm: typeof vVal.cm === 'number' ? vVal.cm : null,
				address: typeof vVal.addr === 'string' && vVal.addr.length > 0 ? vVal.addr : null,
				arrondissement: typeof vVal.arr === 'string' && vVal.arr.length > 0 ? vVal.arr : null,
			})
		}
		fDetails = vMap
		fLoading = null
		return vMap
	})()
	return fLoading
}

/*
Synchronous accessor. Returns null when the map isn't loaded yet OR
when there are no extras for the given arbreId (a tree whose CSV row
had no height / circ / address). Caller treats null as "no details
to display" rather than an error.
*/
export function detailsFor(inArbreId: number): ArbreDetails | null
{
	if (!fDetails) return null
	return fDetails.get(inArbreId) ?? null
}
